import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { Paper, Chip, ButtonGroup, Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import { formatDateTime, getRoleName, showDeleteResults } from '@utils/helpers';
import API from '@/utils/axios';
import theme from '@/theme';
import { useAuth } from '@contexts/AuthContext';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useDialog } from '@contexts/DialogContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import OurPagination from '@components/OurPagination';
import OurDataToolbar from '@components/OurDataToolbar';
import UserSearch from '@components/user/UserSearch';

function UserTable() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { doLogout } = useAuth();
    const { showMessage } = useSnackbar();
    const { requestConfirm } = useDialog();
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useLocalStorage('users.pagination', {
        pageSize: 10,
        page: 0,
    });
    const [sortModel, setSortModel] = useLocalStorage('users.sort', [{
        field: 'id',
        sort: 'desc',
    }]);
    const [columnVisibility, setColumnVisibility] = useLocalStorage('users.columns', {
        first_name: false,
        last_name: false,
        lang: false,
        customer_id: false,
        created_at: false,
        updated_at: false,
    });
    const [density, setDensity] = useLocalStorage('users.density', 'standard');
    const [searchVisible, setSearchVisible] = useLocalStorage('users.searchVisible', false);
    const [filter, setFilter] = useLocalStorage('users.searchValues', {});
    const [selectedRows, setSelectedRows] = useState([]);

    const openEditPage = (params) => {
        navigate(`/users/${params.id}`);
    };

    const toggleSearch = () => {
        if (searchVisible) {
            setFilter({});
        }
        setSearchVisible(!searchVisible);
    };

    const getFullName = (value, row) => {
        return `${row.first_name || ''} ${row.last_name || ''}`;
    };

    const handleDelete = async () => {
        requestConfirm({
            message: t('Do you really want to delete the selected users?'), 
            onConfirm: async () => {
                try {
                    const results = await API.delete(`/users/batch`, { data: { ids: selectedRows } });
                    showDeleteResults(results, showMessage, t, '{{nb}} user deleted successfully', '{{nb}} users deleted successfully');
                    loadUsers(paginationModel, sortModel); // Refresh
                } catch (error) {
                    if (error.response && error.response.status === 401) {
                        doLogout();
                    } else {
                        showMessage(t(error.message), 'error');
                    }
                }
            }
        });
    };

    const loadUsers = useCallback(async (paginationModel, sortModel) => {
        try {
            setLoading(true);
            const { field = '', sort = '' } = sortModel[0] || {};
            let params = {
                per_page: paginationModel.pageSize,
                page: paginationModel.page + 1,
                sort: field,
                direction: sort,
            };
            const response = await API.get('/users', { params: { ...params, ...filter } });
            setUsers(
                response.data.data.map((user) => ({
                    id: user.id,
                    first_name: user.first_name,
                    last_name: user.last_name,
                    email: user.email,
                    active: user.active,
                    last_login: user.last_login ? new Date(user.last_login) : null,
                    lang: user.lang,
                    role: user.role,
                    customer_id: user.customer_id,
                    customer_name: user.customer_name,
                    created_at: new Date(user.created_at),
                    updated_at: new Date(user.updated_at),
                }))
            );
            setRowCount(response.data.total);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.status === 401) {
                doLogout();
            } else {
                console.error('Erreur de chargement', error);
            }
        }
    }, [doLogout, filter]);

    useEffect(() => {
        loadUsers(paginationModel, sortModel);
    }, [loadUsers, paginationModel, sortModel]);

    const localeTheme = i18n.language === 'fr' ? frFR : enUS;
    const themeWithLocale = createTheme(theme, localeTheme);

    const columns = [
        {
            field: 'id',
            headerName: t('ID'),
            flex: 0.5,
            type: 'number',
            maxWidth: 70,
        },
        {
            field: 'first_name',
            headerName: t('First name'),
            flex: 1,
            type: 'string'
        },
        {
            field: 'last_name',
            headerName: t('Last name'),
            flex: 1,
            type: 'string'
        },
        {
            field: 'full_name',
            headerName: t('Full name'),
            flex: 2,
            type: 'string',
            valueGetter: getFullName
        },
        {
            field: 'email',
            headerName: t('Email'),
            flex: 2,
            type: 'string'
        },
        {
            field: 'active',
            headerName: t('Active|user'),
            flex: 1,
            type: 'boolean',
        },
        {
            field: 'last_login',
            headerName: t('Last login'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        },
        {
            field: 'lang',
            headerName: t('Language'),
            flex: 1,
            type: 'string',
            valueFormatter: (lang) => t('lang.' + lang),
        },
        {
            field: 'role',
            headerName: t('Role'),
            flex: 1,
            type: 'string',
            valueFormatter: (role) => getRoleName(t, role),
            renderCell: (params) => (
                <Chip
                    size="small"
                    label={getRoleName(t, params.value)}
                    color={params.row.role === 1 ? 'warning' : params.row.role === 2 ? 'primary' : 'default'}
                />
            ),
        },
        {
            field: 'customer_id',
            headerName: t('Customer ID'),
            flex: 1,
            type: 'number'
        },
        {
            field: 'customer_name',
            headerName: t('Customer'),
            flex: 2,
            type: 'string'
        },
        {
            field: 'created_at',
            headerName: t('Creation date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        },
        {
            field: 'updated_at',
            headerName: t('Update date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        }
    ];

    return (
        <ThemeProvider theme={themeWithLocale}>
            <Paper>
                {searchVisible && <UserSearch handleSubmit={setFilter} filter={filter} />}
                <DataGrid
                    autoHeight
                    loading={loading}
                    slots={{
                        toolbar: OurDataToolbar,
                        pagination: OurPagination,
                    }}
                    slotProps={{
                        toolbar: { searchVisible, toggleSearch },
                    }}
                    initialState={{
                        sorting: { sortModel }
                    }}
                    rows={users}
                    columns={columns}
                    pageSizeOptions={[10, 15, 20, 50, 100]}
                    pagination
                    paginationMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    columnVisibilityModel={columnVisibility}
                    onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibility(newModel)
                    }
                    sortingMode="server"
                    sortingOrder={['asc', 'desc']}
                    onSortModelChange={setSortModel}
                    density={density}
                    onDensityChange={setDensity}
                    disableRowSelectionOnClick
                    disableSelectionOnClick
                    disableColumnResize={true}
                    disableColumnFilter
                    checkboxSelection
                    onRowClick={openEditPage}
                    onRowSelectionModelChange={(newSelectionModel) => {
                        setSelectedRows(newSelectionModel);
                    }}
                />

            </Paper>

            <ButtonGroup variant="contained" aria-label="Actions" sx={{ mt: 2 }}>
                <Button onClick={handleDelete} startIcon={<DeleteIcon />}
                    disabled={selectedRows.length === 0}>
                    {t('Delete')}
                </Button>
            </ButtonGroup>
        </ThemeProvider>
    );
}

export default UserTable;
